import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  APEX_BASE_URL,
  SITE_NAME,
  getIslandUrl,
  getSiteBackgroundSpec,
} from 'alakai-aloha-shared';
import { PublicSiteBackground } from 'alakai-aloha-shared/public-ui';
import AppLink from '../shared/AppLink';

const SATELLITE_IMAGE_SRC = '/apex/hawaiian-islands-satellite.jpg';
const APEX_LOGO_SRC = '/apex/alakai-aloha-logo-white.svg';
const MAP_WIDTH = 2402;
const MAP_HEIGHT = 1671;
const MAP_ASPECT_RATIO = MAP_WIDTH / MAP_HEIGHT;
const COMPACT_LANDSCAPE_MEDIA_QUERY = '(orientation: landscape) and (max-height: 900px)';

const ISLAND_LINKS = [
  {
    key: 'kauai',
    islandKey: 'kauai',
    label: 'Kauai',
    href: getIslandUrl('kauai', '/'),
    previewImageSrc: '/apex/island-previews/kauai.jpg',
    spotlight: { cx: 379, cy: 199, r: 128 },
    points:
      '276,212 277,201 278,196 304,155 305,154 361,124 363,123 371,122 403,120 437,120 457,126 460,128 474,142 481,158 481,170 465,238 455,251 437,266 424,276 417,279 415,279 352,269 349,268 288,232 277,216 276,213',
  },
  {
    key: 'oahu',
    islandKey: 'oahu',
    label: 'Oahu',
    href: getIslandUrl('oahu', '/'),
    previewImageSrc: '/apex/island-previews/oahu.jpg',
    spotlight: { cx: 1029, cy: 452, r: 146 },
    points:
      '901,410 1005,357 1017,352 1025,352 1028,353 1033,357 1034,358 1126,464 1156,531 1156,533 1155,535 1137,550 1096,552 1091,552 970,534 966,532 965,531 919,454 902,413',
  },
  {
    key: 'maui',
    islandKey: 'maui',
    label: 'Maui',
    href: getIslandUrl('maui', '/'),
    previewImageSrc: '/apex/island-previews/maui.jpg',
    spotlight: { cx: 1703, cy: 754, r: 168 },
    points:
      '1555,701 1556,691 1561,679 1566,669 1568,666 1569,665 1579,657 1595,654 1598,654 1742,696 1747,699 1841,758 1847,764 1850,769 1850,791 1849,794 1847,798 1843,804 1841,806 1823,822 1790,834 1722,851 1695,853 1682,853 1676,851 1666,844 1664,842 1584,750 1567,730 1563,725 1560,720 1555,703',
  },
  {
    key: 'big-island',
    islandKey: 'big_island',
    label: 'Big Island',
    href: getIslandUrl('big_island', '/'),
    previewImageSrc: '/apex/island-previews/big-island.jpg',
    spotlight: { cx: 2086, cy: 1294, r: 292 },
    points:
      '1828,1233 1831,1212 1890,1008 1898,996 1904,994 1924,998 2152,1106 2174,1122 2187,1132 2207,1149 2343,1326 2344,1333 2340,1346 2329,1358 2286,1393 1988,1594 1985,1593 1903,1539 1890,1517 1830,1246 1828,1236',
  },
];

const FOOTER_LINKS = [
  { label: 'Blog', href: '/blog' },
  { label: 'About Us', href: '/about' },
  { label: 'Contact Us', href: '/support' },
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Terms of Use', href: '/terms' },
];

export default function ApexHomepage() {
  const backgroundTheme = useMemo(() => getSiteBackgroundSpec('apex'), []);
  const [featuredIslandKey, setFeaturedIslandKey] = useState(ISLAND_LINKS[0].key);
  const [isCompactLandscape, setIsCompactLandscape] = useState(() => (
    typeof window !== 'undefined'
      ? window.matchMedia(COMPACT_LANDSCAPE_MEDIA_QUERY).matches
      : false
  ));
  const previewRailRef = useRef(null);
  const previewCardRefs = useRef({});
  const programmaticScrollTimeoutRef = useRef(null);
  const scrollSettleTimeoutRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);

  const centerPreviewCard = useCallback((islandKey, { behavior = 'smooth' } = {}) => {
    const rail = previewRailRef.current;
    const previewCard = previewCardRefs.current[islandKey];
    if (!rail || !previewCard) {
      return;
    }

    const targetScrollLeft = previewCard.offsetLeft - ((rail.clientWidth - previewCard.clientWidth) / 2);
    const maxScrollLeft = Math.max(rail.scrollWidth - rail.clientWidth, 0);
    const clampedScrollLeft = Math.min(Math.max(targetScrollLeft, 0), maxScrollLeft);

    isProgrammaticScrollRef.current = true;
    if (programmaticScrollTimeoutRef.current) {
      window.clearTimeout(programmaticScrollTimeoutRef.current);
    }

    rail.scrollTo({
      left: clampedScrollLeft,
      behavior,
    });

    programmaticScrollTimeoutRef.current = window.setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, behavior === 'auto' ? 0 : 450);
  }, []);

  useEffect(() => {
    centerPreviewCard(ISLAND_LINKS[0].key, { behavior: 'auto' });
  }, [centerPreviewCard]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const compactLandscapeQuery = window.matchMedia(COMPACT_LANDSCAPE_MEDIA_QUERY);
    const updateCompactLandscape = (event) => {
      setIsCompactLandscape(event.matches);
    };

    setIsCompactLandscape(compactLandscapeQuery.matches);

    if (compactLandscapeQuery.addEventListener) {
      compactLandscapeQuery.addEventListener('change', updateCompactLandscape);
    } else {
      compactLandscapeQuery.addListener(updateCompactLandscape);
    }

    return () => {
      if (compactLandscapeQuery.removeEventListener) {
        compactLandscapeQuery.removeEventListener('change', updateCompactLandscape);
      } else {
        compactLandscapeQuery.removeListener(updateCompactLandscape);
      }
    };
  }, []);

  useEffect(() => {
    const rail = previewRailRef.current;
    if (!rail) {
      return undefined;
    }

    const updateFeaturedIsland = () => {
      if (isProgrammaticScrollRef.current) {
        return;
      }

      const railRect = rail.getBoundingClientRect();
      const railCenterX = railRect.left + railRect.width / 2;
      let nearestIslandKey = ISLAND_LINKS[0].key;
      let nearestDistance = Number.POSITIVE_INFINITY;

      for (const island of ISLAND_LINKS) {
        const card = previewCardRefs.current[island.key];
        if (!card) {
          continue;
        }

        const cardRect = card.getBoundingClientRect();
        const cardCenterX = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(railCenterX - cardCenterX);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIslandKey = island.key;
        }
      }

      setFeaturedIslandKey((currentKey) =>
        currentKey === nearestIslandKey ? currentKey : nearestIslandKey,
      );
    };

    const handleRailScroll = () => {
      if (scrollSettleTimeoutRef.current) {
        window.clearTimeout(scrollSettleTimeoutRef.current);
      }

      scrollSettleTimeoutRef.current = window.setTimeout(updateFeaturedIsland, 140);
    };

    rail.addEventListener('scroll', handleRailScroll, { passive: true });

    return () => {
      rail.removeEventListener('scroll', handleRailScroll);
      if (scrollSettleTimeoutRef.current) {
        window.clearTimeout(scrollSettleTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (programmaticScrollTimeoutRef.current) {
        window.clearTimeout(programmaticScrollTimeoutRef.current);
      }
      if (scrollSettleTimeoutRef.current) {
        window.clearTimeout(scrollSettleTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let resizeFrame = null;
    const handleResize = () => {
      if (resizeFrame) {
        window.cancelAnimationFrame(resizeFrame);
      }

      resizeFrame = window.requestAnimationFrame(() => {
        centerPreviewCard(featuredIslandKey, { behavior: 'auto' });
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeFrame) {
        window.cancelAnimationFrame(resizeFrame);
      }
    };
  }, [centerPreviewCard, featuredIslandKey]);

  const featureIsland = (islandKey, { scrollPreview = false, scrollBehavior = 'smooth' } = {}) => {
    setFeaturedIslandKey((currentKey) => (currentKey === islandKey ? currentKey : islandKey));

    if (!scrollPreview) {
      return;
    }

    centerPreviewCard(islandKey, { behavior: scrollBehavior });
  };

  const previewRailStyle = useMemo(() => ({
    '--preview-card-width': isCompactLandscape
      ? 'clamp(220px, 24vw, 320px)'
      : 'clamp(320px, 56vw, 760px)',
    '--preview-rail-padding': isCompactLandscape
      ? 'max(0.75rem, calc((100vw - var(--preview-card-width)) / 2))'
      : 'max(1rem, calc((100vw - var(--preview-card-width)) / 2))',
    '--preview-arrow-offset': isCompactLandscape ? '2.75rem' : '4rem',
  }), [isCompactLandscape]);
  const mapMaxHeight = isCompactLandscape
    ? 'clamp(16rem, calc(100svh - 14.5rem), 34rem)'
    : '66svh';
  const mapFrameStyle = useMemo(() => ({
    aspectRatio: `${MAP_WIDTH} / ${MAP_HEIGHT}`,
    width: `min(100%, calc(${mapMaxHeight} * ${MAP_ASPECT_RATIO}))`,
    maxHeight: mapMaxHeight,
  }), [mapMaxHeight]);
  const featuredIsland = ISLAND_LINKS.find((island) => island.key === featuredIslandKey) || ISLAND_LINKS[0];
  const featuredIslandIndex = ISLAND_LINKS.findIndex((island) => island.key === featuredIslandKey);
  const featuredSpotlight = {
    ...featuredIsland.spotlight,
    cutoutRadius: Math.round(featuredIsland.spotlight.r * 1.45),
    haloRadius: Math.round(featuredIsland.spotlight.r * 1.72),
  };
  const canGoToPreviousIsland = featuredIslandIndex > 0;
  const canGoToNextIsland = featuredIslandIndex < ISLAND_LINKS.length - 1;
  const moveFeaturedIsland = (direction) => {
    const nextIsland = ISLAND_LINKS[featuredIslandIndex + direction];
    if (!nextIsland) {
      return;
    }

    featureIsland(nextIsland.key, { scrollPreview: true });
  };

  return (
    <>
      <Helmet>
        <title>{`${SITE_NAME} | Choose Your Island`}</title>
        <meta
          name="description"
          content="Choose Kauai, Oahu, Maui, or the Big Island from a satellite view of the Hawaiian islands and continue to the corresponding Alakai Aloha island site."
        />
        <link rel="canonical" href={`${APEX_BASE_URL}/`} />
      </Helmet>

      <div className="relative flex min-h-screen flex-col overflow-hidden bg-slate-950 text-white">
        <PublicSiteBackground backgroundTheme={backgroundTheme} />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.18) 1px, transparent 1px)',
            backgroundSize: '120px 120px',
            maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.9), rgba(0,0,0,0.15))',
          }}
        />

        <h1 className="sr-only">Choose your island</h1>

        <main className="relative z-10 flex flex-1 flex-col">
          <div className="w-full lg:px-6 lg:pt-6 xl:px-8 xl:pt-8">
            <div
              className="mx-auto w-full overflow-hidden shadow-[0_32px_90px_-28px_rgba(0,0,0,0.82)] lg:max-w-[88rem] lg:rounded-lg"
              style={mapFrameStyle}
            >
              <svg
                viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
                preserveAspectRatio="xMidYMid meet"
                className="block h-full w-full"
                role="group"
                aria-labelledby="hawaii-map-title hawaii-map-description"
              >
                <defs>
                  <radialGradient id="featured-island-cutout-gradient">
                    <stop offset="0%" stopColor="#000000" />
                    <stop offset="46%" stopColor="#000000" />
                    <stop offset="100%" stopColor="#ffffff" />
                  </radialGradient>
                  <radialGradient id="featured-island-halo-gradient">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.12" />
                    <stop offset="56%" stopColor="#ffffff" stopOpacity="0.035" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                  </radialGradient>
                  <mask id="featured-island-cutout">
                    <rect x="0" y="0" width={MAP_WIDTH} height={MAP_HEIGHT} fill="white" />
                    <circle
                      cx={featuredSpotlight.cx}
                      cy={featuredSpotlight.cy}
                      r={featuredSpotlight.cutoutRadius}
                      fill="url(#featured-island-cutout-gradient)"
                    />
                  </mask>
                </defs>
                <title id="hawaii-map-title">Satellite view of the Hawaiian islands</title>
                <desc id="hawaii-map-description">
                  Satellite image of Niihau, Kauai, Oahu, Molokai, Lanai, Maui, Kahoolawe, and the Big Island.
                  Kauai, Oahu, Maui, and the Big Island can be selected from the map to feature their cards below.
                </desc>
                <image
                  href={SATELLITE_IMAGE_SRC}
                  width={MAP_WIDTH}
                  height={MAP_HEIGHT}
                  preserveAspectRatio="xMidYMid meet"
                />
                <rect
                  x="0"
                  y="0"
                  width={MAP_WIDTH}
                  height={MAP_HEIGHT}
                  fill="#031018"
                  fillOpacity="0.4"
                  mask="url(#featured-island-cutout)"
                  pointerEvents="none"
                />
                <image
                  href={APEX_LOGO_SRC}
                  x="64"
                  y="724"
                  width="1120"
                  height="1120"
                  opacity="0.92"
                  preserveAspectRatio="xMidYMid meet"
                  pointerEvents="none"
                />
                <circle
                  cx={featuredSpotlight.cx}
                  cy={featuredSpotlight.cy}
                  r={featuredSpotlight.haloRadius}
                  fill="url(#featured-island-halo-gradient)"
                  pointerEvents="none"
                />
                {ISLAND_LINKS.map((island) => {
                  const isFeatured = island.key === featuredIslandKey;

                  return (
                    <g
                      key={island.key}
                      role="button"
                      aria-label={isFeatured ? `${island.label}, selected` : `Select ${island.label}`}
                      tabIndex={0}
                      style={{ outline: 'none' }}
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseEnter={() => featureIsland(island.key, { scrollPreview: true })}
                      onFocus={() => featureIsland(island.key, { scrollPreview: true })}
                      onClick={() => featureIsland(island.key, { scrollPreview: true })}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          featureIsland(island.key, { scrollPreview: true });
                        }
                      }}
                    >
                      <title>{isFeatured ? `${island.label} selected` : `Select ${island.label}`}</title>
                      <polygon
                        points={island.points}
                        style={{
                          fill: 'rgba(255, 255, 255, 0.001)',
                          stroke: 'transparent',
                          strokeWidth: 0,
                          strokeLinejoin: 'round',
                          strokeLinecap: 'round',
                          pointerEvents: 'all',
                          cursor: 'pointer',
                        }}
                      />
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>

          <section
            className={`relative ${isCompactLandscape ? 'pb-2 pt-3' : 'pb-10 pt-6'}`}
            style={previewRailStyle}
          >
            {canGoToPreviousIsland ? (
              <button
                type="button"
                aria-label="Show previous island card"
                className="absolute top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/18 bg-black/28 text-white/88 shadow-[0_18px_40px_-20px_rgba(0,0,0,0.9)] backdrop-blur-sm transition hover:border-white/32 hover:bg-black/42 hover:text-white md:flex"
                style={{ left: 'calc(50% - (var(--preview-card-width) / 2) - var(--preview-arrow-offset))' }}
                onClick={() => moveFeaturedIsland(-1)}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                  <path
                    d="M14.5 5.5L8 12l6.5 6.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ) : null}
            {canGoToNextIsland ? (
              <button
                type="button"
                aria-label="Show next island card"
                className="absolute top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/18 bg-black/28 text-white/88 shadow-[0_18px_40px_-20px_rgba(0,0,0,0.9)] backdrop-blur-sm transition hover:border-white/32 hover:bg-black/42 hover:text-white md:flex"
                style={{ right: 'calc(50% - (var(--preview-card-width) / 2) - var(--preview-arrow-offset))' }}
                onClick={() => moveFeaturedIsland(1)}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                  <path
                    d="M9.5 5.5L16 12l-6.5 6.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ) : null}
            <div
              ref={previewRailRef}
              className={`flex snap-x snap-mandatory overflow-x-auto pl-[var(--preview-rail-padding)] pr-[var(--preview-rail-padding)] [scroll-padding-inline:var(--preview-rail-padding)] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${
                isCompactLandscape ? 'gap-2.5 pb-1 pt-0.5 sm:gap-3' : 'gap-4 pb-5 pt-2 sm:gap-6'
              }`}
            >
              {ISLAND_LINKS.map((island) => {
                const isFeatured = island.key === featuredIslandKey;

                return (
                  <AppLink
                    key={island.key}
                    ref={(node) => {
                      previewCardRefs.current[island.key] = node;
                    }}
                    to={island.href}
                    aria-label={`Open the ${island.label} island site`}
                    className={`group relative block w-[var(--preview-card-width)] shrink-0 snap-center [scroll-snap-stop:always] overflow-hidden rounded-lg transition-all duration-300 ${
                      isFeatured
                        ? 'scale-100 opacity-100 shadow-[0_0_0_1px_rgba(255,255,255,0.16),0_0_54px_-14px_rgba(132,202,255,0.72),0_34px_72px_-24px_rgba(0,0,0,0.86)]'
                        : 'scale-[0.96] opacity-68 shadow-[0_24px_50px_-28px_rgba(0,0,0,0.72)]'
                    }`}
                  >
                    <div className={`relative overflow-hidden rounded-lg ${isCompactLandscape ? 'aspect-[16/9]' : 'aspect-[5/4]'}`}>
                      <img
                        src={island.previewImageSrc}
                        alt=""
                        className={`h-full w-full object-cover transition-transform duration-500 ${
                          isFeatured ? 'scale-[1.025]' : 'scale-100'
                        }`}
                      />
                      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(6,12,21,0.08)_0%,rgba(6,12,21,0.14)_34%,rgba(6,12,21,0.52)_100%)]" />
                      <div className="absolute inset-0 ring-1 ring-white/10" />

                      <div className="absolute inset-x-0 top-0 flex justify-end px-4 pt-4 text-right sm:px-7 sm:pt-6">
                        <span className={`font-semibold tracking-[0.02em] text-white ${
                          isCompactLandscape ? 'text-base sm:text-[1.35rem]' : 'text-lg sm:text-[1.65rem]'
                        }`}>
                          {island.label}
                        </span>
                      </div>
                    </div>
                  </AppLink>
                );
              })}
            </div>
            <div className="mx-auto mt-2 flex max-w-5xl justify-center px-5">
              <AppLink
                to="/quiz/choose-island"
                className="inline-flex items-center justify-center rounded-full border border-emerald-300/35 bg-emerald-300/12 px-5 py-3 text-center text-sm font-semibold text-emerald-100 transition hover:border-emerald-200/60 hover:bg-emerald-300/18"
              >
                Not sure which island to visit? Answer a few questions and get our recommendation.
              </AppLink>
            </div>
          </section>
        </main>

        <footer className={`relative z-10 px-6 text-center text-xs text-white/78 sm:text-sm ${
          isCompactLandscape ? 'pb-3 pt-2' : 'pb-5 pt-5'
        }`}>
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <span>© 2026 Alakai Aloha</span>
            <span>|</span>
            {FOOTER_LINKS.map((link, index) => (
              <React.Fragment key={link.href}>
                <AppLink to={link.href} className="underline-offset-2 hover:text-white hover:underline">
                  {link.label}
                </AppLink>
                {index < FOOTER_LINKS.length - 1 ? <span>|</span> : null}
              </React.Fragment>
            ))}
          </div>
        </footer>
      </div>
    </>
  );
}
